import { OIDC_CLAIM_MAPPING_FIELDS, OIDC_DEFAULT_CLAIM_MAPPING, OIDC_REQUIRED_SCOPE, OIDC_SCOPES_MAX, type OidcMappedField } from "@sagot/constants";
import type { OidcSettings, OidcSettingsBody } from "./use-oidc-settings";

/** Claim name per mapped field, as typed. */
export type OidcMapping = Readonly<Record<OidcMappedField, string>>;

export interface OidcDraft {
  issuer: string;
  clientId: string;
  /** Write-only: empty means "keep the one stored". */
  clientSecret: string;
  /** Space or comma separated, without `openid` (it is always sent). */
  scopes: string;
  mapping: OidcMapping;
  active: boolean;
}

export type OidcProblem = "issuerInvalid" | "issuerHttp" | "clientIdMissing" | "secretMissing" | "scopesTooMany" | "mappingMissing";

/** Hosts the development and test stacks reach their mock providers on; production accepts https only. */
const DEV_HOSTS: readonly string[] = ["localhost", "127.0.0.1", "host.docker.internal"];

/** The scopes the page edits: everything but `openid`, which the server always puts first. */
export function scopesText(scopes: readonly string[]): string {
  return scopes.filter((scope) => scope !== OIDC_REQUIRED_SCOPE).join(" ");
}

export function splitScopes(raw: string): string[] {
  const seen = new Set<string>();
  for (const part of raw.split(/[\s,]+/)) {
    const scope = part.trim();
    if (scope !== "" && scope !== OIDC_REQUIRED_SCOPE) seen.add(scope);
  }
  return [...seen];
}

/** Same scopes whatever the order they were typed in. */
export function sameScopes(a: string, b: string): boolean {
  return splitScopes(a).sort().join(" ") === splitScopes(b).sort().join(" ");
}

export function mappingOf(raw: Readonly<Record<string, string>>): OidcMapping {
  const entries = OIDC_CLAIM_MAPPING_FIELDS.map((field) => [field, raw[field] ?? OIDC_DEFAULT_CLAIM_MAPPING[field]] as const);
  return Object.fromEntries(entries) as OidcMapping;
}

export function draftOf(settings: OidcSettings): OidcDraft {
  return {
    issuer: settings.issuer ?? "",
    clientId: settings.client_id ?? "",
    clientSecret: "",
    scopes: scopesText(settings.scopes),
    mapping: mappingOf(settings.claim_mapping),
    active: settings.configured ? settings.active : true,
  };
}

export function sameMapping(a: OidcMapping, b: OidcMapping): boolean {
  return OIDC_CLAIM_MAPPING_FIELDS.every((field) => a[field].trim() === b[field].trim());
}

export function isDirty(draft: OidcDraft, saved: OidcSettings): boolean {
  const base = draftOf(saved);
  return (
    draft.issuer.trim() !== base.issuer ||
    draft.clientId.trim() !== base.clientId ||
    draft.clientSecret !== "" ||
    !sameScopes(draft.scopes, base.scopes) ||
    !sameMapping(draft.mapping, base.mapping) ||
    draft.active !== base.active
  );
}

/** What the page can tell before sending; the server repeats every rule and also checks the provider can be reached. */
export function problemsOf(draft: OidcDraft, saved: OidcSettings): OidcProblem[] {
  const found: OidcProblem[] = [];
  const issuer = draft.issuer.trim();
  try {
    const url = new URL(issuer);
    if (url.protocol !== "https:" && !(url.protocol === "http:" && DEV_HOSTS.includes(url.hostname))) found.push("issuerHttp");
  } catch {
    found.push("issuerInvalid");
  }
  if (draft.clientId.trim() === "") found.push("clientIdMissing");
  if (!saved.has_secret && draft.clientSecret.trim() === "") found.push("secretMissing");
  if (splitScopes(draft.scopes).length + 1 > OIDC_SCOPES_MAX) found.push("scopesTooMany");
  if (OIDC_CLAIM_MAPPING_FIELDS.some((field) => draft.mapping[field].trim() === "")) found.push("mappingMissing");
  return found;
}

export function toBody(draft: OidcDraft): OidcSettingsBody {
  return {
    issuer: draft.issuer.trim(),
    client_id: draft.clientId.trim(),
    ...(draft.clientSecret.trim() !== "" ? { client_secret: draft.clientSecret.trim() } : {}),
    scopes: splitScopes(draft.scopes),
    claim_mapping: Object.fromEntries(OIDC_CLAIM_MAPPING_FIELDS.map((field) => [field, draft.mapping[field].trim()])),
    active: draft.active,
  };
}
